import { useCallback, useEffect, useMemo, useState } from 'react';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import arrowLeft from '~/assets/icons/arrow_left.svg';
import timer from '~/assets/icons/timer.svg';
import { Sound, playSound } from '~/assets/sounds';
import { CollectionImage } from '~/components/collections';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { Text } from '~/components/ui/Text';
import { rage, useObservable } from '~/rage';
import { CaseType } from '~/shared';
import {
	setActiveCase,
	setShopKitOpen,
	setShopOpenCaseVisible,
	setTabIndex,
	useLayoutDispatch,
} from '../../redux';
import { formatTime } from '../../utils';
import { bannerVariations } from './MainBanner.model';
import {
	StyledBannerContent,
	StyledBottomContainer,
	StyledCaseBgContainer,
	StyledCaseImageContainer,
	StyledContainer,
	StyledDot,
	StyledLeftContainer,
	StyledSideButton,
	StyledTimerContainer,
} from './MainBanner.styled';

const MainBanner = () => {
	const dispatch = useLayoutDispatch();
	const [index, setIndex] = useState(0);
	const [isPaused, setIsPaused] = useState(false);
	const cases = useObservable(rage.cases.getLootboxes());
	// const bundles = useObservable(rage.cases.getBundles());
	const [endTime, setEndTime] = useState({ time: '', unit: '' });

	const filteredCases = useMemo(
		() => cases.filter((item) => item.showOnBanner),
		[cases],
	);

	// @TODO: Добавить наборы на баннер, если будет дизайн
	// const filteredBundles = useMemo(
	// 	() => bundles.filter((item) => item.showOnBanner),
	// 	[bundles],
	// );

	const handlePrev = useCallback(() => {
		setIndex((prevState) =>
			prevState === 0 ? filteredCases.length - 1 : prevState - 1,
		);
	}, [filteredCases]);

	const handleNext = useCallback(() => {
		setIndex((prevState) =>
			prevState === filteredCases.length - 1 ? 0 : prevState + 1,
		);
	}, [filteredCases]);

	const handleClick = useCallback(async () => {
		if (!filteredCases[index]?.id) {
			return;
		}

		const selectedCase = rage.cases.getLootboxById(filteredCases[index].id);
		const { getClientConfig: _getClientConfig, ...caseWithoutConfig } =
			selectedCase as any;
		dispatch(setActiveCase(caseWithoutConfig));
		dispatch(setTabIndex(1));
		if (rage.cases.getCaseTypeById(selectedCase.id) === CaseType.CASE) {
			dispatch(setShopOpenCaseVisible(true));
		} else {
			dispatch(setShopKitOpen(true));
		}
	}, [filteredCases, index]);

	const countTime = useCallback(() => {
		if (
			!filteredCases[index]?.enabledTimestampEnd ||
			filteredCases[index]?.enabledTimestampEnd === undefined
		) {
			return;
		}

		const time = formatTime(filteredCases[index].enabledTimestampEnd || null);
		setEndTime(time);
	}, [filteredCases, index]);

	useEffect(() => {
		if (isPaused) return;

		const interval = setInterval(() => {
			handleNext();
		}, 7000);

		return () => clearInterval(interval);
	}, [handleNext, isPaused]);

	useEffect(() => {
		countTime();
		const interval = setInterval(countTime, 1000);
		return () => clearInterval(interval);
	}, [countTime]);

	return (
		<StyledContainer
			$color={'#215954'}
			onMouseEnter={() => setIsPaused(true)}
			onMouseLeave={() => setIsPaused(false)}
		>
			<SwitchTransition>
				<CSSTransition
					timeout={300}
					classNames={'case'}
					key={index}
					addEndListener={(node, done) =>
						node.addEventListener('transitionend', done, false)
					}
				>
					<StyledCaseBgContainer>
						<CollectionImage
							collection={'cases'}
							id={filteredCases[index]?.id ?? ''}
							size={512}
						/>
					</StyledCaseBgContainer>
				</CSSTransition>
			</SwitchTransition>
			<StyledSideButton
				style={{ left: 20 }}
				onMouseEnter={() => playSound(Sound.LinkCursor)}
				onClick={() => {
					handlePrev();
					playSound(Sound.ClickButton);
				}}
			>
				<img src={arrowLeft} />
			</StyledSideButton>
			<SwitchTransition>
				<CSSTransition
					timeout={300}
					classNames={'case'}
					key={index}
					addEndListener={(node, done) =>
						node.addEventListener('transitionend', done, false)
					}
				>
					<StyledBannerContent>
						<StyledLeftContainer>
							<Text style={{ fontWeight: 500, fontSize: 16 }}>
								{filteredCases[index]?.name}
							</Text>
							<Text style={{ marginTop: 16, opacity: 0.5, maxWidth: 290 }}>
								{bannerVariations[0].textFirst}
								<br />
								<br />
								{bannerVariations[0].textSecond}
							</Text>
							<StyledBottomContainer>
								<Button
									color={ButtonColors.Select2}
									height={ButtonHeight.Height40}
									onClick={() => {
										handleClick();
										playSound(Sound.ClickButton);
									}}
									style={{ width: 157 }}
								>
									<Text style={{ fontSize: 14 }}>Узнать подробнее</Text>
								</Button>
								<StyledDot
									style={{
										visibility: endTime.time ? 'visible' : 'hidden',
									}}
								/>
								<StyledTimerContainer
									style={{
										visibility: endTime.time ? 'visible' : 'hidden',
									}}
								>
									<Text style={{ opacity: 0.5 }}>До конца акции</Text>
									<img src={timer} />
									<Text>
										<span style={{ fontWeight: 700 }}>{endTime.time}</span>
										{endTime.unit && (
											<span style={{ fontWeight: 400, opacity: 0.5 }}>
												&nbsp;{endTime.unit}
											</span>
										)}
									</Text>
								</StyledTimerContainer>
							</StyledBottomContainer>
						</StyledLeftContainer>
						<StyledCaseImageContainer>
							<CollectionImage
								collection={'cases'}
								id={filteredCases[index]?.id ?? ''}
								size={240}
							/>
						</StyledCaseImageContainer>
					</StyledBannerContent>
				</CSSTransition>
			</SwitchTransition>
			<StyledSideButton
				style={{ right: 20 }}
				onMouseEnter={() => playSound(Sound.LinkCursor)}
				onClick={() => {
					handleNext();
					playSound(Sound.ClickButton);
				}}
			>
				<img src={arrowLeft} style={{ transform: 'rotate(180deg)' }} />
			</StyledSideButton>
		</StyledContainer>
	);
};

export { MainBanner };
